/**
 * Types and predicates for `app.bsky.embed.document` (PDFs and office docs).
 *
 * The installed `@atproto/api` build does not ship generated types for this
 * lexicon yet, so the record/view shapes live here and are shared by the
 * composer, the embed renderer, drafts, and the native module wrapper.
 *
 * The audience fields (`level`, `subject`) are the same curriculum *codes* a
 * capsule or FlashBook carries, so one vocabulary names every piece of school
 * material. They are declared at publish time and travel on the embed record;
 * they are deliberately absent from the served embed *view*, which is why the
 * card reads them off the post record (see `getDocumentAudience`).
 */
import {type AppBskyFeedDefs, BlobRef} from '@atproto/api'

export const DOCUMENT_EMBED_RECORD_TYPE = 'app.bsky.embed.document'
export const DOCUMENT_EMBED_VIEW_TYPE = 'app.bsky.embed.document#view'

export type DocumentBlobRef = {
  $type: 'blob'
  ref: {$link: string}
  mimeType: string
  size: number
}

export type DocumentEmbedRecord = {
  $type: typeof DOCUMENT_EMBED_RECORD_TYPE
  blob: DocumentBlobRef
  mimeType: string
  title?: string
  pageCount?: number
  fileSize?: number
  alt?: string
  /** Curriculum grade code, eg. `Level_B1` (see `#/lib/curriculum`). */
  level?: string
  /** Curriculum subject code, eg. `Maths`. */
  subject?: string
  /**
   * Where this document's page material is, and what each page holds.
   *
   * The address is a companion record — the clips and the questions live there,
   * and a reader fetches it only when they play. The page table is the summary
   * that rides along so every badge can be drawn without that fetch; totals are
   * summed from it rather than stored beside it (see `#/lib/document/material`).
   */
  materialUri?: string
  materialPages?: {page: number; audio: number; questions: number}[]
}

export type DocumentEmbedView = {
  $type: typeof DOCUMENT_EMBED_VIEW_TYPE
  cid: string
  manifestUri?: string
  mimeType: string
  title?: string
  pageCount?: number
  fileSize?: number
  alt?: string
  thumbnailUri?: string | null
  /** Mirrored onto the view once the app view serves it; read from the record. */
  level?: string
  subject?: string
  /** Mirrored onto the view once the app view serves it; read from the record. */
  materialUri?: string
  materialPages?: {page: number; audio: number; questions: number}[]
}

/**
 * Who a document was published for, as the platform's curriculum codes.
 *
 * Both members are codes rather than labels because a reader's language is not
 * the author's — `#/lib/curriculum` turns a code into words at render time.
 */
export type DocumentAudience = {
  level: string
  subject: string
}

/**
 * The audience worth storing, or `undefined` when it is not one.
 *
 * A record field holds whatever it was given, so the rule lives here rather
 * than at the call sites: an audience with half a code on it would render as a
 * chip with half a name, which is worse than showing nothing.
 */
export function normalizeDocumentAudience(
  level: unknown,
  subject: unknown,
): DocumentAudience | undefined {
  if (typeof level !== 'string' || typeof subject !== 'string') return undefined
  const trimmedLevel = level.trim()
  const trimmedSubject = subject.trim()
  if (!trimmedLevel || !trimmedSubject) return undefined
  return {level: trimmedLevel, subject: trimmedSubject}
}

/**
 * The audience of a document embed record or view, if it carries one.
 *
 * The card is handed an embed that came off a *view*, and the served view drops
 * these fields, so this reads whichever of the two it is given — the record
 * when the caller has one (a post always does, via `postView.record`), the view
 * when the app view starts mirroring them.
 */
export function getDocumentAudience(
  embed: unknown,
): DocumentAudience | undefined {
  if (!isDocumentEmbedRecord(embed) && !isDocumentEmbedView(embed)) {
    return undefined
  }
  return normalizeDocumentAudience(embed.level, embed.subject)
}

function isTypedObject(v: unknown, type: string): boolean {
  return (
    typeof v === 'object' &&
    v !== null &&
    (v as {$type?: unknown}).$type === type
  )
}

export function isDocumentEmbedRecord(v: unknown): v is DocumentEmbedRecord {
  return isTypedObject(v, DOCUMENT_EMBED_RECORD_TYPE)
}

export function isDocumentEmbedView(v: unknown): v is DocumentEmbedView {
  return isTypedObject(v, DOCUMENT_EMBED_VIEW_TYPE)
}

/**
 * The audience a *post* declares, read from the post's own record.
 *
 * The served embed view is built from a fixed set of fields, so the curriculum
 * codes are only on the record — and a post view carries its record. Surfaces
 * that render a document are the ones that call this, so that what a reader
 * sees about a document is what its author actually published.
 */
export function documentAudienceFromPost(
  record: unknown,
): DocumentAudience | undefined {
  if (typeof record !== 'object' || record === null) return undefined
  return getDocumentAudience(
    getDocumentEmbedRecord((record as {embed?: unknown}).embed),
  )
}

/**
 * Pulls a document *record* out of a post record's embed, including documents
 * used as the media of a `recordWithMedia` embed (quote post + document). The
 * audience fields only exist here, not on the served view.
 */
export function getDocumentEmbedRecord(
  embed: unknown,
): DocumentEmbedRecord | undefined {
  if (isDocumentEmbedRecord(embed)) {
    return embed
  }
  if (
    typeof embed === 'object' &&
    embed !== null &&
    (embed as {$type?: string}).$type === 'app.bsky.embed.recordWithMedia'
  ) {
    const media = (embed as {media?: unknown}).media
    if (isDocumentEmbedRecord(media)) {
      return media
    }
  }
  return undefined
}

/**
 * The embed view a post draws.
 *
 * A row draws the embed the app view built for the post (`postView.embed`), and the
 * app view builds one for every embed it serves — except that a document can arrive
 * with no view at all, and then the row drew nothing: a document post is a file with
 * no caption, so a reader met a post with no words and no embed on it — a timestamp,
 * an action bar, and nothing between them. A published document read as an empty
 * post.
 *
 * The post's own record is on the post either way, and it holds every field the
 * card's chrome is drawn from: the blob's cid, the file's type, its size, its title
 * and its page count are fields of the record — the *same* fields the publisher
 * wrote (`#/lib/document/publish`). So when no view was served and the record
 * carries a document, the view is built from the record. Nothing is inferred: a
 * record that names no bytes gets no view, and the row goes on drawing what it drew
 * before.
 *
 * The audience and the page material are deliberately *not* copied onto it. The
 * served view does not carry them either, and the surfaces hand them to the card off
 * the record (`documentAudienceFromPost`, `materialRefFromRecord`), so a view built
 * here says exactly as much as one the app view serves.
 *
 * This is the bargain a book post already makes — a served view is preferred where
 * there is one, and a missing one is never allowed to make a published post read as
 * empty (see the `hasFlashbookEmbed` branch in `#/view/com/posts/PostFeedItem`).
 */
export function postEmbedView({
  embed,
  record,
}: {
  /** The embed the app view served on the post, when it served one. */
  embed?: unknown
  /** The post's own record — where a document's view is built from. */
  record?: unknown
}): AppBskyFeedDefs.PostView['embed'] | undefined {
  if (embed) return embed as AppBskyFeedDefs.PostView['embed']
  if (typeof record !== 'object' || record === null) return undefined
  const document = (record as {embed?: unknown}).embed
  /*
   * Only a document the post *is*. A document quoted as the media of a
   * `recordWithMedia` is a document too, but the row draws the quote around it and
   * the quoted post is not on this post's record — a view built from here would draw
   * that post as a bare document and lose the quote. So a quote the app view did not
   * serve stays as it was rather than being redrawn as something it is not.
   */
  if (!isDocumentEmbedRecord(document)) return undefined
  /*
   * The cast is the installed `@atproto/api` build: the union a post row is typed
   * with cannot name `app.bsky.embed.document#view`, which is why this module
   * declares the view itself. The renderer reads the `$type`, not the type.
   */
  return documentViewFromRecord(document) as
    | AppBskyFeedDefs.PostView['embed']
    | undefined
}

/**
 * The cid a blob is stored under — the one string a card's address is built from.
 *
 * A record read through the agent does not hand back the JSON a PDS wrote: a `blob`
 * arrives as a `BlobRef` whose `ref` is a CID *object* rather than the `{$link}` form
 * it was serialized as, so the link a raw record carries is simply absent on it. The
 * cid is therefore read off whichever of the two is in hand, the `BlobRef` case the
 * same way the composer's own uploads read theirs (`blob.ref.toString()` — see
 * `#/lib/document/materialPublish` and `#/components/flashbooks/flashbookSave`).
 *
 * Both forms are real: what the agent hands the app is a `BlobRef`, and what a test
 * or a fixture writes is usually the JSON. A reader that knew only one of them read
 * no cid at all in the app, which is a card that draws nothing.
 */
function blobCidOf(blob: unknown): string | undefined {
  if (blob instanceof BlobRef) return blob.ref.toString()
  if (typeof blob !== 'object' || blob === null) return undefined
  const link = (blob as {ref?: {$link?: unknown}}).ref?.$link
  return typeof link === 'string' && link ? link : undefined
}

/**
 * The view the app view would have built from a document record, field for field.
 *
 * The cid is what the card fetches the bytes by, so a record that names none has no
 * view to draw. Every other field is copied rather than defaulted, so a card never
 * states a page count or a size its author did not publish.
 */
function documentViewFromRecord(
  record: DocumentEmbedRecord,
): DocumentEmbedView | undefined {
  const cid = blobCidOf(record.blob)
  if (!cid) return undefined
  return {
    $type: DOCUMENT_EMBED_VIEW_TYPE,
    cid,
    mimeType: record.mimeType,
    title: record.title,
    pageCount: record.pageCount,
    fileSize: record.fileSize,
    alt: record.alt,
  }
}

/**
 * Pulls a document view out of a post embed, including documents used as the
 * media of a `recordWithMedia` embed (quote post + document).
 */
export function getDocumentEmbedView(
  embed: unknown,
): DocumentEmbedView | undefined {
  if (isDocumentEmbedView(embed)) {
    return embed
  }
  if (
    typeof embed === 'object' &&
    embed !== null &&
    (embed as {$type?: string}).$type === 'app.bsky.embed.recordWithMedia#view'
  ) {
    const media = (embed as {media?: unknown}).media
    if (isDocumentEmbedView(media)) {
      return media
    }
  }
  return undefined
}
